// Weapon trails (decision 0047: sword trails; suggestion "VFX particle kit": slash ribbons from the real blade's base
// and tip). A trail is a ribbon through the last few samples of a weapon's base and tip, fading with age. The game
// starts one when a move's active window opens (`combat:move` phase "active") and stops it after (the ribbon then
// shrinks away), and passes a function that says where a weapon's base and tip are each frame.
//
// Additive blending, coloured by vertex: a sample's brightness falls with its age, so the old end fades to nothing.
// One material for every trail (one program, compiled at boot by drawing a warm trail once).
import * as THREE from "three";

/** Where a weapon is this frame (world space), or null when the owner has none in hand. */
export type WeaponSample = (owner: number) => { base: readonly [number, number, number]; tip: readonly [number, number, number] } | null;

/** The trails' numbers, from the game. */
export interface TrailOptions {
  /** Samples kept per trail (the ribbon's length in frames). */
  samples: number;
  /** A sample's life (s): it fades over this long. */
  life: number;
  /** The ribbon's colour at full brightness. */
  colour: THREE.ColorRepresentation;
}

interface Trail {
  owner: number;
  emitting: boolean;
  pts: { base: THREE.Vector3; tip: THREE.Vector3; age: number }[];
  mesh: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>;
}

/** Live trails for one scene: add `root`, call `update(dt, sample)` once per frame. */
export class Trails {
  readonly root = new THREE.Group();
  private readonly material: THREE.MeshBasicMaterial;
  private live = new Map<number, Trail>();
  private pool: THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial>[] = [];
  private readonly colour: THREE.Color;
  /** Trails started so far. */
  started = 0;

  constructor(readonly options: TrailOptions) {
    this.root.name = "vfx:trails";
    this.colour = new THREE.Color(options.colour);
    this.material = new THREE.MeshBasicMaterial({ vertexColors: true, transparent: true, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide, toneMapped: false });
  }

  private take(): THREE.Mesh<THREE.BufferGeometry, THREE.MeshBasicMaterial> {
    const m = this.pool.pop();
    if (m) return m;
    const n = this.options.samples;
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(new Float32Array(n * 2 * 3), 3));
    g.setAttribute("color", new THREE.BufferAttribute(new Float32Array(n * 2 * 3), 3));
    const idx: number[] = [];
    for (let i = 0; i < n - 1; i++) {
      const a = i * 2;
      idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2);
    }
    g.setIndex(idx);
    g.setDrawRange(0, 0);
    const mesh = new THREE.Mesh(g, this.material);
    mesh.name = "vfx:trail";
    mesh.frustumCulled = false;
    mesh.renderOrder = 3;
    return mesh;
  }

  /** Start (or keep) emitting a trail for an owner's weapon. */
  start(owner: number): void {
    const t = this.live.get(owner);
    if (t) {
      t.emitting = true;
      return;
    }
    const mesh = this.take();
    this.root.add(mesh);
    this.live.set(owner, { owner, emitting: true, pts: [], mesh });
    this.started++;
  }

  /** Stop emitting; the ribbon fades out over its life. */
  stop(owner: number): void {
    const t = this.live.get(owner);
    if (t) t.emitting = false;
  }

  /** Advance by `dt` (the game's choice of clock: scaled, so a hit-stop freezes the ribbon with the blade). */
  update(dt: number, sample: WeaponSample): void {
    const { samples, life } = this.options;
    for (const t of [...this.live.values()]) {
      for (const p of t.pts) p.age += Math.max(0, dt);
      if (t.emitting) {
        const s = sample(t.owner);
        if (s) {
          t.pts.unshift({ base: new THREE.Vector3(...s.base), tip: new THREE.Vector3(...s.tip), age: 0 });
          if (t.pts.length > samples) t.pts.length = samples;
        }
      }
      t.pts = t.pts.filter((p) => p.age < life);
      if (!t.emitting && t.pts.length === 0) {
        t.mesh.removeFromParent();
        this.pool.push(t.mesh);
        this.live.delete(t.owner);
        continue;
      }
      const pos = t.mesh.geometry.getAttribute("position") as THREE.BufferAttribute;
      const col = t.mesh.geometry.getAttribute("color") as THREE.BufferAttribute;
      t.pts.forEach((p, i) => {
        const k = 1 - p.age / life;
        const b = k * k;
        pos.setXYZ(i * 2, p.base.x, p.base.y, p.base.z);
        pos.setXYZ(i * 2 + 1, p.tip.x, p.tip.y, p.tip.z);
        // the base end dimmer than the tip: the blade's edge carries the light
        col.setXYZ(i * 2, this.colour.r * b * 0.25, this.colour.g * b * 0.25, this.colour.b * b * 0.25);
        col.setXYZ(i * 2 + 1, this.colour.r * b, this.colour.g * b, this.colour.b * b);
      });
      pos.needsUpdate = true;
      col.needsUpdate = true;
      t.mesh.geometry.setDrawRange(0, Math.max(0, t.pts.length - 1) * 6);
    }
  }

  /** The live trails: owner, emitting, samples. */
  state(): { owner: number; emitting: boolean; samples: number }[] {
    return [...this.live.values()].map((t) => ({ owner: t.owner, emitting: t.emitting, samples: t.pts.length }));
  }

  /** Free everything. */
  dispose(): void {
    for (const t of this.live.values()) t.mesh.removeFromParent();
    for (const m of [...this.pool, ...[...this.live.values()].map((t) => t.mesh)]) m.geometry.dispose();
    this.live.clear();
    this.pool = [];
    this.material.dispose();
    this.root.removeFromParent();
  }
}
